import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api, BASE } from "../../services/api";

function resolveImg(url) {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

export default function Testimonials() {
    const [reviews, setReviews]   = useState([]);
    const [partners, setPartners] = useState([]);
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        api.getTestimonials().then(data => { if (data?.length) setReviews(data); }).catch(() => {});
        api.getPartners().then(data => { if (data?.length) setPartners(data); }).catch(() => {});
    }, []);

    const next = () => setIdx((i) => (i + 1) % reviews.length);
    const prev = () => setIdx((i) => (i - 1 + reviews.length) % reviews.length);

    if (!reviews.length && !partners.length) return null;

    const review = reviews[idx] ?? null;

    return (
        <section
            id="testimonials"
            className={`relative px-6 sm:px-12 bg-nova-black overflow-hidden ${reviews.length > 0 ? "py-24 sm:py-32" : "py-16 sm:py-20"}`}
            data-testid="testimonials-section"
        >
            <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-nova-purple/15 rounded-full blur-[140px] pointer-events-none" />

            <div className="relative max-w-6xl mx-auto">
                {reviews.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mb-14 max-w-3xl"
                >
                    <div className="font-mono text-xs uppercase tracking-[0.25em] text-nova-cyan mb-4">
                        / 06 — Referencie
                    </div>
                    <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
                        Čo o nás hovoria{" "}
                        <span className="text-nova-cyan">klienti</span>
                    </h2>
                </motion.div>
                )}

                {review && <div className="relative">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.5 }}
                            className="border border-white/8 bg-nova-surface/60 rounded-xl p-10 sm:p-14 relative"
                            data-testid="testimonial-card"
                        >
                            <div className="text-[5rem] leading-none font-heading font-black text-nova-cyan/15 select-none mb-2">
                                "
                            </div>

                            <p className="font-heading text-2xl sm:text-3xl lg:text-[2rem] font-medium text-white leading-[1.35] tracking-tight">
                                {review.quote}
                            </p>

                            <div className="mt-10 pt-8 border-t border-white/8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    {review.company_logo && (
                                        <img
                                            src={resolveImg(review.company_logo)}
                                            alt=""
                                            className="h-7 w-auto max-w-[100px] object-contain opacity-70 shrink-0"
                                        />
                                    )}
                                    <div>
                                        <div className="font-heading font-bold text-white text-base">
                                            {review.author}
                                        </div>
                                        <div className="text-sm text-zinc-500 mt-0.5">
                                            {review.role}
                                        </div>
                                    </div>
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nova-cyan/50 border border-nova-cyan/20 px-3 py-1.5 rounded-sm">
                                    {review.event}
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    <div className="flex items-center justify-between mt-8">
                        <div className="flex gap-2">
                            {reviews.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setIdx(i)}
                                    aria-label={`Prejsť na referenciu ${i + 1}`}
                                    className={`h-1.5 rounded-full transition-all ${
                                        i === idx
                                            ? "w-10 bg-nova-cyan"
                                            : "w-6 bg-white/15 hover:bg-white/30"
                                    }`}
                                    data-testid={`testimonial-dot-${i}`}
                                />
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={prev}
                                className="w-12 h-12 rounded-full border border-white/15 bg-nova-surface/60 hover:bg-nova-surface hover:border-nova-cyan/50 flex items-center justify-center transition-all"
                                data-testid="testimonial-prev"
                                aria-label="Predchádzajúca referencia"
                            >
                                <ChevronLeft className="w-5 h-5 text-white" />
                            </button>
                            <button
                                onClick={next}
                                className="w-12 h-12 rounded-full border border-white/15 bg-nova-surface/60 hover:bg-nova-surface hover:border-nova-cyan/50 flex items-center justify-center transition-all"
                                data-testid="testimonial-next"
                                aria-label="Ďalšia referencia"
                            >
                                <ChevronRight className="w-5 h-5 text-white" />
                            </button>
                        </div>
                    </div>
                </div>}

                {/* CPP Promo House — úzka spolupráca */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className={`${(review || partners.length > 0) ? "mt-16 pt-12 border-t border-white/6" : ""}`}
                >
                    <div className="font-mono text-sm uppercase tracking-[0.2em] text-zinc-400 text-center mb-8">
                        Úzka spolupráca
                    </div>
                    <div className="flex justify-center">
                        <a
                            href="https://cpppromo.sk/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex flex-col items-center gap-4 px-10 py-6 rounded-2xl border border-white/6 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/12 transition-all"
                        >
                            <img
                                src="/cpp-promo-house.svg"
                                alt="CPP Promo House"
                                className="h-12 sm:h-16 w-auto opacity-60 group-hover:opacity-90 transition-opacity duration-300"
                            />
                            <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600 group-hover:text-zinc-400 transition-colors">
                                cpppromohouse.sk
                            </span>
                        </a>
                    </div>
                </motion.div>

                {/* Partners logo strip */}
                {partners.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mt-14"
                    >
                        <div className="font-mono text-sm uppercase tracking-[0.2em] text-zinc-400 text-center mb-10">
                            Dôverujú nám
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-10 sm:gap-16">
                            {partners.map(p => (
                                <div key={p.id} className="flex items-center justify-center" style={{ width: 160, height: 56 }}>
                                    <img
                                        src={resolveImg(p.logo_url)}
                                        alt={p.name}
                                        width={160}
                                        height={56}
                                        className="h-10 sm:h-14 w-auto max-w-[160px] object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
                                    />
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </div>
        </section>
    );
}
